import {StyleSettings} from "../../models/remoteConstants.model";

// Mirror of the client's baked SO asset values
// (Rift/Assets/ScriptableObjects/Styles/*.asset). Used to lazy-seed the Styles
// tab so authoring starts from what the game already shows. MUST stay in sync
// with those assets: the client falls back to the same baked values when a
// branch omits `styles`, so a mismatch here would make first-time authoring
// visibly change the look.
const rgb = (r: number, g: number, b: number, a = 1) => ({r, g, b, a});

export const DEFAULT_STYLES: StyleSettings = {
    tileStyle: {
        fogColor: rgb(1, 1, 1, 1),
        fogStrength: 0.5,
        fogDesaturation: 0.5,
        fogNoiseScale: 4,
        fogNoiseSpeed: 0.5,
        fogSwirl: 0.8,
        fogNoiseAmount: 0.5,
        fogNoiseBrightness: 0.25,
        movementHighlight: {
            addColor: rgb(0, 1, 0.12156863, 0.11372549),
            mulColor: rgb(0, 1, 0.12013221, 0.6745098),
            lerpColor: rgb(0, 1, 0.12156863, 0.0627451),
        },
        cloudHighlight: {
            addColor: rgb(0, 1, 0.12156863, 0.23921569),
            mulColor: rgb(0.27612138, 0.6886792, 0.3262755, 0.84313726),
            lerpColor: rgb(0, 1, 0.12156863, 0.12156863),
        },
        fogHighlight: {
            addColor: rgb(0, 1, 0.12156863, 0.05882353),
            mulColor: rgb(0.13238695, 0.8018868, 0.21377717, 0.6392157),
            lerpColor: rgb(0, 1, 0.12156863, 0.19215687),
        },
        cityHighlight: {
            addColor: rgb(0, 0.79365087, 1, 0.12941177),
            mulColor: rgb(0.7216981, 1, 0.95546734, 0.44705883),
            lerpColor: rgb(0, 0.6413455, 1, 0.09019608),
        },
    },
    uiStyle: {
        unsetPipColor: rgb(0.7169812, 0.7169812, 0.7169812, 1),
        setPipColor: rgb(0, 0.5429063, 1, 1),
        deficitPipColor: rgb(0.85, 0.15, 0.15, 1),
        interactableColor: rgb(1, 1, 1, 1),
        // Not serialized in UISettings.asset — the class default is Color.gray.
        disabledColor: rgb(0.5, 0.5, 0.5, 1),
        backgroundColor: rgb(0.08627451, 0.105882354, 0.13333334, 1),
        textColor: rgb(1, 1, 1, 1),
        attributeColorInfoBar: rgb(0.08627451, 0.105882354, 0.13333334, 1),
        lockedNode: rgb(0.16470589, 0.19607843, 0.23921569, 1),
        unlockedNode: rgb(0.25882354, 0.3137255, 0.39607844, 1),
        researchedNode: rgb(0.2509804, 0.6039216, 0.65882355, 1),
        purchasedNode: rgb(0.29411766, 0.65882355, 0.2509804, 1),
        // Not serialized in UISettings.asset — these are the class defaults in
        // UISettings.cs: rgb(126,146,168) for the lines, and one shared rgb(140,190,240)
        // for all four node outlines at 1 canvas unit.
        techTreeLine: rgb(0.49411765, 0.57254905, 0.65882355, 1),
        lockedNodeBorder: rgb(0.54901963, 0.74509805, 0.9411765, 1),
        unlockedNodeBorder: rgb(0.54901963, 0.74509805, 0.9411765, 1),
        researchedNodeBorder: rgb(0.54901963, 0.74509805, 0.9411765, 1),
        purchasedNodeBorder: rgb(0.54901963, 0.74509805, 0.9411765, 1),
        // Node name text: white for every state, matching UISettings.cs.
        lockedNodeText: rgb(1, 1, 1, 1),
        unlockedNodeText: rgb(1, 1, 1, 1),
        researchedNodeText: rgb(1, 1, 1, 1),
        purchasedNodeText: rgb(1, 1, 1, 1),
        techTreeNodeBorderWidth: 1,
        // Must match the class defaults in UISettings.cs. A mismatch makes first-time authoring
        // visibly change the chart's colours for no reason the author asked for.
        chartColorLightnessMin: 0.5,
        chartColorLightnessMax: 0.66,
        chartColorChromaMax: 0.15,
        chartDividerWidth: 0,
        // Option discs. Not serialized in UISettings.asset — these are the class
        // defaults in UISettings.cs. Produce keeps the disc's historical grey and a full
        // circle; the three action categories share a squircle, because their tints
        // already separate which kind of action it is. Unit actions start on the produce
        // grey since they never share a panel with the others.
        produceOptionTint: rgb(0.58, 0.63, 0.67, 1),
        buildingActionOptionTint: rgb(0.67, 0.58, 0.51, 1),
        tileActionOptionTint: rgb(0.55, 0.66, 0.56, 1),
        unitActionOptionTint: rgb(0.58, 0.63, 0.67, 1),
        selectedCargoOptionTint: rgb(0.85, 0.89, 0.93, 1),
        produceOptionCornerRadius: 0.5,
        buildingActionOptionCornerRadius: 0.3,
        tileActionOptionCornerRadius: 0.3,
        unitActionOptionCornerRadius: 0.3,
    },
    unitStyle: {
        normalHPColor: rgb(1, 1, 1, 1),
        criticalHPColor: rgb(1, 0, 0, 1),
        // Must match UnitSettings.cs's baked defaults, or first-time authoring
        // visibly changes the timing.
        meleeAttackDuration: 0.3,
        rangedProjectileDuration: 0.3,
    },
};
